import React, { useMemo } from 'react';
import type { BlueprintFormField } from '../../../types/processBlueprint';
import type { ExecutionField } from '../../../types/card';
import { isFieldValueEmpty } from '../utils/pendenciesUtils';

type FormField = BlueprintFormField | ExecutionField;

interface MinhaMesaFormFieldsProps {
  fields: FormField[];
  formState: Record<string, unknown>;
  onFieldChange: (fieldId: string, value: unknown) => void;
  readOnly?: boolean;
  invalidFieldIds?: string[];
  variant?: 'default' | 'execution';
}

const SECTION_SHELLS: string[] = [];

function normalizeGroupName(name: string, index: number): string {
  const trimmed = name.trim();
  if (!trimmed || /^novo\s*grupo$/i.test(trimmed)) {
    const defaults = ['Identificação', 'Materiais', 'Validação', 'Complementos'];
    return defaults[index] ?? `Seção ${index + 1}`;
  }
  return trimmed;
}

export const MinhaMesaFormFields: React.FC<MinhaMesaFormFieldsProps> = ({
  fields,
  formState,
  onFieldChange,
  readOnly = false,
  invalidFieldIds = [],
  variant = 'default',
}) => {
  const isExecution = variant === 'execution';
  if (fields.length === 0) {
    return null;
  }

  const firstFocusableFieldId = useMemo(() => {
    const firstRequiredEmpty = fields.find((field) => {
      if (!field.required || readOnly) return false;
      return isFieldValueEmpty(formState[field.id] ?? field.defaultValue);
    });
    return firstRequiredEmpty?.id ?? null;
  }, [fields, formState, readOnly]);

  const baseInputClass = isExecution
    ? 'w-full border-0 border-b border-slate-200 bg-transparent px-0 py-2 text-[13px] text-slate-800 transition focus:border-blue-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200'
    : 'w-full rounded-[4px] border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 transition hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';

  const pendingFieldIds = useMemo(
    () =>
      new Set(
        fields
          .filter((field) => field.required && isFieldValueEmpty(formState[field.id] ?? field.defaultValue))
          .map((field) => field.id),
      ),
    [fields, formState],
  );

  const groups = useMemo(() => {
    const map = new Map<string, FormField[]>();
    for (const field of fields) {
      const g = (field as { groupName?: string }).groupName ?? '';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(field);
    }
    return Array.from(map.entries());
  }, [fields]);

  const renderField = (field: FormField) => {
    const value = formState[field.id] ?? field.defaultValue ?? '';
    const ph = (field as { placeholder?: string }).placeholder;
    const autoFocus = field.id === firstFocusableFieldId;
    const isPending = pendingFieldIds.has(field.id) || invalidFieldIds.includes(field.id);

    return (
      <div
        key={field.id}
        data-field-id={field.id}
        className={`scroll-mt-24 transition-colors ${
          isExecution
            ? `border-b border-slate-100 py-2 dark:border-slate-800/80 ${isPending ? 'border-l-2 border-l-amber-400 pl-3' : 'pl-1'}`
            : `rounded-sm p-1.5 ${field.type === 'textarea' ? 'lg:col-span-2' : ''} ${
                isPending ? 'border border-amber-200/60 bg-amber-50/30 dark:border-amber-900/30 dark:bg-amber-950/10' : ''
              }`
        }`}
      >
        <label
          className={
            isExecution
              ? 'mb-0.5 block text-[12px] font-medium text-slate-700 dark:text-slate-300'
              : 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-700 dark:text-slate-300'
          }
        >
          {field.label}
          {field.required && !isExecution && <span className="ml-0.5 text-rose-500">*</span>}
          {isPending && (
            <span className={`ml-1.5 text-[9px] font-medium ${isExecution ? 'text-amber-600' : 'font-bold normal-case tracking-normal text-amber-700 dark:text-amber-300'}`}>
              {isExecution ? 'pendente' : 'Pendente'}
            </span>
          )}
        </label>
        {(field as { description?: string }).description && (
          <p className="mb-1 text-[10px] text-slate-500 dark:text-slate-400">{(field as { description?: string }).description}</p>
        )}

        {field.type === 'text' && (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            placeholder={ph ?? 'Preencha este campo'}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'number' && (
          <input
            type="number"
            value={value === '' ? '' : Number(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value === '' ? '' : Number(e.target.value))}
            disabled={readOnly}
            placeholder={ph ?? 'Preencha este campo'}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'currency' && (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
            <input
              type="number"
              step="0.01"
              value={value === '' ? '' : Number(value)}
              onChange={(e) => onFieldChange(field.id, e.target.value === '' ? '' : Number(e.target.value))}
              disabled={readOnly}
              placeholder={ph}
              autoFocus={autoFocus}
              className={`${baseInputClass} pl-9`}
            />
          </div>
        )}

        {field.type === 'date' && (
          <input
            type="date"
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'textarea' && (
          <textarea
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            rows={3}
            placeholder={ph}
            autoFocus={autoFocus}
            className={baseInputClass}
          />
        )}

        {field.type === 'select' && (
          <select
            value={String(value)}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={readOnly}
            autoFocus={autoFocus}
            className={baseInputClass}
          >
            <option value="">Selecione...</option>
            {(field.options ?? []).map((opt) => {
              const val = typeof opt === 'string' ? opt : opt.value;
              const lbl = typeof opt === 'string' ? opt : opt.label;
              return <option key={val} value={val}>{lbl}</option>;
            })}
          </select>
        )}

        {field.type === 'checkbox' && (
          <label className="flex items-center gap-2 rounded-[4px] border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onFieldChange(field.id, e.target.checked)}
              disabled={readOnly}
              autoFocus={autoFocus}
              className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
            />
            <span className="text-[12px] text-slate-600 dark:text-slate-400">Sim</span>
          </label>
        )}

        {field.type === 'file' && (
          <input
            type="file"
            disabled={readOnly}
            onChange={() => {/* TODO: implementar upload */}}
            className="text-sm text-slate-500 file:mr-4 file:rounded-[4px] file:border file:border-slate-200 file:bg-slate-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:file:border-neutral-700 dark:file:bg-blue-900/30 dark:file:text-blue-400"
          />
        )}
      </div>
    );
  };

  const hasGroups = groups.length > 1 || groups.some(([g]) => g !== '');

  if (isExecution) {
    const renderGroup = (groupFields: FormField[], sectionLabel?: string) => (
      <div key={sectionLabel ?? 'default'} className="space-y-0">
        {sectionLabel && (
          <p className="mb-2 mt-4 text-[9px] font-bold uppercase tracking-wider text-slate-400">{sectionLabel}</p>
        )}
        {groupFields.map(renderField)}
      </div>
    );

    if (!hasGroups) return <div>{fields.map(renderField)}</div>;

    return (
      <div>
        {groups.map(([groupName, groupFields], index) =>
          renderGroup(groupFields, normalizeGroupName(groupName, index)),
        )}
      </div>
    );
  }

  if (!hasGroups) {
    return (
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 lg:grid-cols-2">
        {fields.map(renderField)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(([groupName, groupFields], index) => {
        const sectionLabel = normalizeGroupName(groupName, index);
        const pendingCount = groupFields.filter(
          (field) => field.required && isFieldValueEmpty(formState[field.id] ?? field.defaultValue),
        ).length;

        return (
          <section key={groupName || `section-${index}`} className={SECTION_SHELLS[index % SECTION_SHELLS.length]}>
            <header className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5 dark:border-slate-800">
              <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">{sectionLabel}</span>
              {pendingCount > 0 && (
                <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[7px] font-bold uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                  {pendingCount} pend.
                </span>
              )}
            </header>
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 lg:grid-cols-2">{groupFields.map(renderField)}</div>
          </section>
        );
      })}
    </div>
  );
};
